import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';

import { ChatList } from '#modules/chat/components/Chats/ChatList';
import { Discover } from '#modules/finder/features/posts/components/Discover';
import { DiscoverLayout } from '#modules/finder/features/posts/components/Discover/DiscoverLayout';
import { Feed } from '#modules/user/features/dashboard/components/Feed';
import { Friends } from '#modules/user/features/friends/components/Friends';
import { BottomNavigation } from '#features/layout/components/BottomNavigation';
import { HeaderView } from '#features/layout/components/Header';
import { requestGuardedNavigation } from '#features/layout/model/guardedNavigation';
import { mobileScreen } from '#features/layout/model/mobileNavigation';
import { Button } from '#features/ui';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '#features/ui/components/Dialog';

const screens = [
  'home',
  'discover',
  'friends',
  'chats',
  'post',
  'settings',
  'create',
  'step',
] as const;
type Screen = (typeof screens)[number];
export const Route = createFileRoute('/{-$locale}/_layout/preview/mobile-navigation')({
  ssr: false,
  staticData: { rendersOwnFooter: true, rendersOwnHeader: true },
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    previewLocale: search.previewLocale === 'en' ? 'en' : 'ru',
    screen: screens.includes(search.screen as Screen) ? (search.screen as Screen) : 'discover',
    long: search.long === true || search.long === 'true',
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const { messages } =
      deps.previewLocale === 'en'
        ? await import('#features/i18n/locales/en/messages.po')
        : await import('#features/i18n/locales/ru/messages.po');
    return { ...deps, messages };
  },
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: Preview,
});
function Preview() {
  const data = Route.useLoaderData();
  const i18n = setupI18n({
    locale: data.previewLocale,
    messages: { [data.previewLocale]: data.messages },
  });
  return (
    <I18nProvider i18n={i18n}>
      <NavigationFixture key={`${data.screen}:${data.previewLocale}`} {...data} />
    </I18nProvider>
  );
}
function NavigationFixture({
  screen,
  previewLocale,
  long,
}: {
  screen: Screen;
  previewLocale: string;
  long: boolean;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [result, setResult] = useState('');
  const paths: Record<Screen, string> = {
    home: '/dashboard',
    discover: '/dashboard/discover',
    friends: '/dashboard/friends',
    chats: '/dashboard/chats',
    post: '/posts/preview',
    settings: '/dashboard/settings/devices',
    create: '/dashboard/discover/create',
    step: '/dashboard/discover/create',
  };
  const title = long
    ? previewLocale === 'ru'
      ? 'Очень длинное название игровой сессии с друзьями'
      : 'A very long title for a gaming session with friends'
    : screen === 'friends'
      ? t({ id: 'friends:title', message: 'Friends' })
      : screen === 'chats'
        ? t`Chats`
        : screen === 'settings'
          ? t`media:check.title`
          : screen === 'post'
            ? t`Post`
            : screen === 'create' || screen === 'step'
              ? t`post:postCreation`
              : t`Discover`;
  return (
    <Box
      position="fixed"
      inset={0}
      zIndex="sticky"
      bg="bg"
      overflowY="auto"
      data-navigation-preview
    >
      <HeaderView
        isBrowser
        isAuth
        isLanding={false}
        mobile={{
          screen: mobileScreen(paths[screen]),
          title,
          previousStep: screen === 'step',
          back: () => setResult('Back'),
          cancel: () => setResult('Cancel'),
        }}
      />
      {screen === 'home' ? (
        <Box px={3} pt={{ base: 3, lg: 5 }}>
          <Feed />
        </Box>
      ) : screen === 'discover' ? (
        <Box px={3} pt={{ base: 3, lg: 5 }}>
          <DiscoverLayout>
            <Discover />
          </DiscoverLayout>
        </Box>
      ) : screen === 'friends' ? (
        <Box px={3} pt={{ base: 3, lg: 5 }}>
          <Friends />
        </Box>
      ) : screen === 'chats' ? (
        <Box px={3} pt={{ base: 3, lg: 5 }}>
          <ChatList
            chats={[]}
            chatsStatus="success"
            chatId={undefined}
            hasNoChats
            hasMoreChats={false}
            isLoadingMoreChats={false}
            loadMoreChats={() => undefined}
            isMobileThread={false}
            tab="ALL"
            onTabChange={() => undefined}
            hasTabs
            chatCounts={undefined}
          />
        </Box>
      ) : null}
      <Stack p={4} gap={4} maxW="lg" mx="auto">
        <Text>
          DEV · mobile navigation · {previewLocale} · {screen}
        </Text>
        <Flex gap={2} wrap="wrap">
          {screens.map((next) => (
            <Button key={next} variant="outline" size="sm" asChild>
              <a href={`?previewLocale=${previewLocale}&screen=${next}&long=${long}`}>{next}</a>
            </Button>
          ))}
          <Button size="sm" asChild>
            <a
              href={`?previewLocale=${previewLocale === 'ru' ? 'en' : 'ru'}&screen=${screen}&long=${long}`}
            >
              RU / EN
            </a>
          </Button>
          <Button size="sm" asChild>
            <a href={`?previewLocale=${previewLocale}&screen=${screen}&long=${!long}`}>
              Long title
            </a>
          </Button>
        </Flex>
        <Text role="status">{result}</Text>
        <Button
          onClick={() => {
            setDraft('');
            setOpen(true);
          }}
        >{t`dashboard:reporting.title`}</Button>
      </Stack>
      <BottomNavigation hidden={screen === 'create' || screen === 'step'} />
      <DialogRoot open={open} onOpenChange={({ open }) => setOpen(open)}>
        <DialogContent>
          <DialogHeader title={t`dashboard:reporting.title`} />
          <DialogBody data-native-back-dirty={draft.length > 0}>
            <textarea
              aria-label={t`dashboard:reporting.describe`}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              style={{ width: '100%', minHeight: 200 }}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={(event) => {
                const scope = event.currentTarget.closest('[role="dialog"]');
                if (scope)
                  requestGuardedNavigation(
                    () =>
                      document.dispatchEvent(
                        new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })
                      ),
                    scope
                  );
              }}
            >
              System Back
            </Button>
            <Button disabled>{t`common:form.send`}</Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  );
}
